import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import AurAccordion, {
  AurAccordionRef,
} from '../../common/aur-accordion/AurAccordion';
import {Workspace, WorkspaceStatus} from './model';
import AurButton from '../../common/aur-button/AurButton';
import AurSvgIcon from '../../common/aur-svg-icon/AurSvgIcon';
import AurIcon, {IconNames} from '../../common/aur-icon/AurIcon';
import {Box, Button, Grid, Typography} from '@mui/material';
import {t} from 'i18next';
import {RootState} from '../../../redux/store';
import {useSelector} from 'react-redux';
import {ReportScreenTabTypes} from '../reports/ProjectOverview';
import {
  setPreviousReportTypeTab,
  setProcessingTab,
  setReportPageOpenFrom,
  storeProjectNWorkSpaceId,
  storeSelectedDataSet,
} from '../../../redux/slices/reportsSlice';
import {
  getEvidenceSourcesByWorkspaceId,
  getExportReportXlsx,
} from '../../../api/ReportingManagement';
import {
  downloadResponseAsXLSX,
  getFilenameFromHeaders,
  getUTCDateTimeForDisplay,
} from '../../../utils/utils';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {launchWorkspace} from '../../../api/LaunchPointService';
import {isRelativityDetailValid} from './LaunchWorkspaceMenu';
import AurAlertCarousel from '../../common/aur-alert/AurAlertCarousel';
import {setModalDropDownSelectedItem} from '../../../redux/slices/invoiceModalSlice';
import navConstants from '../../../constants/navConstants';
import {
  continueNCloseDialog,
  setOpenCount,
} from '../../../redux/slices/reportsDialogSlice';
import {
  setSelectedProjectId,
  setSelectedWorkspaceId,
  setStartScroll,
} from '../../../redux/slices/projectOverviewSlice';
import {AurLightTooltip} from '../../common/aur-light-tooltip/aur-light-tooltip';
import {setReportSrc} from '../../../redux/slices/analyticsSlice';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';
import {ReactComponent as ExportIcon} from '../../../assests/svg/export-icon.svg';
import {useAnalytics} from '../../../hooks/useAnalytics';

interface IProps {
  workspaceDetails: Workspace;
  projectId: string | undefined;
  projectName: string;
  workSpaceLevel?: number;
  projectCode?: string;
  onExpand?: () => void;
  onCollapse?: () => void;
  onDeleteAlert?: (alertId: number) => void;
  ecaEnabled?: boolean;
  alerts?: any;
  hostingUsers?: number;
  productionsCompleted?: number;
  hostingUserSummaryDate?: string;
}

export const WorkspaceOverViewTestId = {
  MEDIA_EVIDENCE_CARD_BUTTON: 'media-evidence-card-buton',
  HOSTING_USER_CARD_BUTTON: 'hosting-user-card-button',
  DATA_SET_PROCESSING_CARD_BUTTON: 'datas-set-processing-card-button',
  DATA_VOLUME_REVIEW_CARD_BUTTON: 'data-volume-review-card-button',
  PROCESSED_DATA_SET_CARD_BUTTON: 'processed-data-set-card-button',
  PRODUCTION_COMPLETED_CARD_BUTTON: 'production-completed-card-button',
  TOTAL_USER_UNDER_MANAGEMENT_POPUP_BUTTON:
    'total-user-under-management-pop-up-button',
};

const WorkspaceOverView: React.FC<IProps> = (props: IProps) => {
  const {
    workspaceDetails,
    projectId,
    projectName,
    projectCode,
    onExpand,
    onCollapse,
    alerts,
    onDeleteAlert,
    hostingUsers,
    productionsCompleted,
    hostingUserSummaryDate,
  } = props;

  const {trackGAEvent} = useAnalytics();
  const dispatch = useDispatch();
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const [isClearAllLoading, setIsClearAllLoading] = useState<boolean>(false);
  const [datasetLoading, setDatasetLoading] = useState(false);

  const selectedWorkspaceId = useSelector(
    (state: RootState) => state.projectOverview.selectedWorkspaceId
  );
  const startScroll = useSelector(
    (state: RootState) => state.projectOverview.startScroll
  );

  const {canViewUserSummaryReports, canViewProductionMetricsReports} =
    usePermissionsByPage(PAGE_NAME.PROJECT_OVERVIEW, projectId);

  const navigateToReport = (url: string, states?: any) => {
    dispatch(setReportSrc(GA_REGIONS.BODY));
    if (states) {
      navigate(url, states);
    } else {
      navigate(url);
    }
  };

  const reportTypeSelection = (tabName: string) => {
    const nameMapping: {[key: string]: ReportScreenTabTypes} = {
      hostingVolume: ReportScreenTabTypes.HOSTING_VOLUME,
      HighLevelProductionMetrics: ReportScreenTabTypes.PRODUCTION_METRIC,
      intakeAuditReport: ReportScreenTabTypes.INTAKE_AUDIT_REPORT,
      ProcessingExceptions: ReportScreenTabTypes.PROCESSED_DATASET,
      ProcessingUserSummary: ReportScreenTabTypes.PROCESSED_DATASET,
      HostingUserSummary: ReportScreenTabTypes.HOSTING_USERS,
    };

    return nameMapping[tabName] || undefined;
  };

  const navigationButtonHandler = async (
    alertType: string,
    dataSetEvidence: string
  ) => {
    if (alertType === t('welcomeScreenSingleClient.Invoice')) {
      dispatch(
        setModalDropDownSelectedItem({
          id: projectId ?? workspaceDetails.projectId,
          name: projectName,
        })
      );
      navigateToReport(navConstants.reports.viewInvoices);
    } else if (alertType === t('welcomeScreenSingleClient.ConceirgeReport')) {
      dispatch(
        setModalDropDownSelectedItem({
          id: projectId ?? workspaceDetails.projectId,
          name: projectName,
          code: projectCode,
        })
      );
      navigateToReport(navConstants.reports.conciergeReports, {
        state: {clientId, projectId: projectId ?? workspaceDetails.projectId},
      });
    } else if (
      alertType === t('welcomeScreenSingleClient.processingExceptions') ||
      alertType === t('welcomeScreenSingleClient.ProcessingUserSummary')
    ) {
      if (alertType === t('welcomeScreenSingleClient.processingExceptions')) {
        dispatch(setProcessingTab(1));
      } else {
        dispatch(setProcessingTab(0));
      }
      try {
        setDatasetLoading(true);
        const queryParam = {
          clientId,
          projectId: projectId ?? workspaceDetails.projectId,
          pageSize: '10',
          pageNumber: '1',
          SearchTerm: dataSetEvidence,
        };

        const response = await getEvidenceSourcesByWorkspaceId(
          'workspaceId',
          new URLSearchParams(queryParam).toString()
        );
        setDatasetLoading(false);
        dispatch(
          storeSelectedDataSet({
            projectId: projectId ?? workspaceDetails.projectId,
            workspaceId: 'workspaceId',
            projectName: projectName ?? '',
            workspaceName: '',
            workspaceDataSetName: response.data.data[0].dataset ?? '',
            worksapceDataSetMediaId: response.data.data[0].mediaId ?? '',
          })
        );
        dispatch(continueNCloseDialog());
        dispatch(setOpenCount(2));
      } catch (e) {
        console.error(e);
      } finally {
        setDatasetLoading(false);
        handleCardClick(reportTypeSelection(alertType));
      }
    } else {
      handleCardClick(reportTypeSelection(alertType));
    }
  };

  const handleDownloadSummaryReportXlsx = async () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.FILE_DOWNLOAD,
      file_extension: GA_FILE_EXTENTIONS.XLSX,
      click: {
        name: t('gaTracking.global.export'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.projectViewTab.exportProjectDetails'),
      },
      subPage: {
        subpage_name: t('gaTracking.projectViewTab.projectViewWorkspace'),
        subpage_type: GA_SUBPAGE_TYPE.ACCORDION,
      },
      hasProjectData: true,
    });
    setIsClearAllLoading(true);
    try {
      const response = await getExportReportXlsx(
        clientId as string,
        projectId as string,
        workspaceDetails.workspaceId,
        '/report-summary'
      );
      const fileName =
        getFilenameFromHeaders(response.headers['content-disposition']) ??
        `${workspaceDetails.workspaceId}_${Date.now()}`;
      downloadResponseAsXLSX(response, fileName, false);
    } catch (error: any) {
      console.error(error?.message);
    } finally {
      setIsClearAllLoading(false);
    }
  };
  const accordionRef = useRef<AurAccordionRef>(null);
  const workspaceContainerRef = useRef<HTMLDivElement>(null);
  const [showExportCSV, setShowExportCSV] = useState<boolean>(false);
  const navigate = useNavigate();

  const isWorkspaceClosed = useMemo(() => {
    return workspaceDetails.statusType == WorkspaceStatus.closed;
  }, [workspaceDetails]);

  const titleAction = (
    <Box className="workspace-btn-group">
      {showExportCSV && (
        <AurButton
          type="text"
          size="Medium"
          onClick={handleDownloadSummaryReportXlsx}
          label={t('welcomeScreenSingleClient.exportAsXlsx')}
          dataTestId="workspace-export-csv-btn"
          startIcon={
            isClearAllLoading ? (
              <AurLoadingIndicator size={16} className="progress-icon" />
            ) : (
              <ExportIcon aria-hidden="true" />
            )
          }
        />
      )}
      {isRelativityDetailValid(
        workspaceDetails.instanceId,
        workspaceDetails.artifactID
      ) &&
        workspaceDetails.userHasAccess &&
        !isWorkspaceClosed && (
          <AurButton
            endIcon={<AurIcon icon={IconNames.OpenInNewIcon} />}
            type="text"
            size="Small"
            label={t('welcomeScreenSingleClient.launchInSightline')}
            dataTestId="launch-in-sightline-csv-btn"
            onClick={() =>
              launchWorkspace(
                workspaceDetails.instanceId,
                workspaceDetails.artifactID
              )
            }
          />
        )}
    </Box>
  );

  const updateProjectWorkspaceState = (
    projectId: any,
    workspaceId: any,
    scrollState: boolean
  ) => {
    dispatch(setSelectedProjectId(projectId));
    dispatch(setSelectedWorkspaceId(workspaceId));
    dispatch(setStartScroll(scrollState));
  };

  const handleCardClick = useCallback(
    (tabType: ReportScreenTabTypes) => {
      updateProjectWorkspaceState(
        projectId ?? workspaceDetails.projectId,
        workspaceDetails.workspaceId,
        false
      );
      dispatch(
        storeProjectNWorkSpaceId({
          projectId: projectId ?? workspaceDetails.projectId,
          workspaceId: workspaceDetails.workspaceId,
          projectName: projectName,
          workspaceName: workspaceDetails.name,
          projectCode: projectCode,
        })
      );
      dispatch(setPreviousReportTypeTab(undefined));
      dispatch(setReportSrc(GA_REGIONS.BODY));
      dispatch(setReportPageOpenFrom('/welcome-single-client'));
      navigateToReport(`/reports?tab=${tabType}`);
    },
    [dispatch, projectId, workspaceDetails]
  );

  const titleContent = (
    <Box
      className={`${isWorkspaceClosed ? 'closed mist-700 ' : ''} d-flex align-items-baseline`}
    >
      <Typography className="typo-text subhead2_size">
        {` ${workspaceDetails.workspaceCode} - ${workspaceDetails.name}`}
      </Typography>
      {isWorkspaceClosed && (
        <Box className="closed-chip">
          <AurSvgIcon src="./images/document_broken.svg" />
          <Typography className="typo-text body2_size">
            {t('welcomeScreenSingleClient.destroyed')}
          </Typography>
        </Box>
      )}
    </Box>
  );

  // Use for only component expanding
  useEffect(() => {
    selectedWorkspaceId && selectedWorkspaceId == workspaceDetails.workspaceId
      ? accordionRef.current?.expand()
      : accordionRef.current?.collapse();
  }, []);

  // Trigger after component rendered
  useEffect(() => {
    if (
      selectedWorkspaceId &&
      selectedWorkspaceId == workspaceDetails.workspaceId &&
      startScroll
    ) {
      updateProjectWorkspaceState(null, null, false);
      workspaceContainerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [startScroll]);

  return (
    <Box className="workspace-overview" ref={workspaceContainerRef}>
      <AurAccordion
        showPointerCursorWithinTitle
        collapsed={true}
        onToggle={() => {
          setShowExportCSV(!showExportCSV);
        }}
        onExpand={() => {
          onExpand && onExpand();
        }}
        onCollapse={() => {
          onCollapse && onCollapse();
        }}
        alertWarningIcon={alerts?.length > 0}
        alertCount={
          (workspaceDetails?.workspaceUserAlertCount ?? 0) > 0
            ? (workspaceDetails?.workspaceUserAlertCount ?? 0).toString()
            : undefined
        }
        title={titleContent}
        disabled={isWorkspaceClosed}
        titleContent={titleAction}
        ref={accordionRef}
        toggleButtonTestId="workspace-accordion-toggle"
      >
        <AurLoadingIndicator show={datasetLoading} />
        <Box className="welcome-alert">
          {alerts?.length > 0 ? (
            <div>
              <span id="hosting-user-summary" className="addUserHint">
                {t('reports.hostingUserSummary.alertHints')}
              </span>
              <div
                data-testid="alert"
                aria-describedby={'hosting-user-summary'}
                aria-roledescription="carousel"
                aria-live="polite"
              >
                <AurAlertCarousel
                  titleAlignment={'left'}
                  alertsData={
                    alerts?.map((alert: any) => ({
                      ...alert,
                      workspaceName: workspaceDetails?.name ?? '',
                      deleteButtonHandler: () =>
                        onDeleteAlert?.(alert.userAlertId),
                      prevButtonHandler: () =>
                        navigationButtonHandler(
                          alert.reportType,
                          alert?.datasets?.[0] ?? ''
                        ),
                      showArrowCTA: true,
                      showDatasets: alert?.datasets?.length > 0,
                    })) as []
                  }
                />
              </div>
            </div>
          ) : null}
        </Box>

        <Grid container className="summery-tiles">
          {/** Production Completed */}
          {
            <Grid
              xs={6}
              className={`tile ${!canViewProductionMetricsReports ? 'no-pointer' : ''}`}
            >
              <Box className="sliding">
                <Typography className="typo-text body1_size">
                  {t('welcomeScreenSingleClient.productionsCompletedHover')}
                </Typography>
              </Box>
              <Box className="d-inline-flex">
                <Typography className="typo-text display3_size">
                  {productionsCompleted ?? 0}
                </Typography>
              </Box>
              {canViewProductionMetricsReports && (
                <Button
                  className="button-icon button-exit buttonSmall_Size"
                  onClick={handleCardClick.bind(
                    this,
                    ReportScreenTabTypes.PRODUCTION_METRIC
                  )}
                  data-testid={
                    WorkspaceOverViewTestId.PRODUCTION_COMPLETED_CARD_BUTTON
                  }
                >
                  <AurIcon icon={IconNames.ChevronRight} />
                </Button>
              )}
              <Box className="d-flex">
                <Typography className="typo-text body2_size mist-700">
                  {t('welcomeScreenSingleClient.productionsCompleted')}
                </Typography>
              </Box>
            </Grid>
          }
          {/** Hosting Users */}
          {
            <Grid
              xs={5.8}
              className={`tile ${!canViewUserSummaryReports ? 'no-pointer' : ''}`}
            >
              <Box className="sliding">
                <Typography className="typo-text body1_size">
                  {t('welcomeScreenSingleClient.reviewWorkspaceUsersHover')}
                </Typography>
              </Box>
              <Box className="d-inline-flex">
                <Typography className="typo-text display3_size">
                  {hostingUsers ?? 0}
                </Typography>
                <Typography className="typo-text headline3_size mist-700">
                  †
                </Typography>
              </Box>
              {canViewUserSummaryReports && (
                <Button
                  className="button-icon button-exit buttonSmall_Size"
                  onClick={handleCardClick.bind(
                    this,
                    ReportScreenTabTypes.HOSTING_USERS
                  )}
                  data-testid={WorkspaceOverViewTestId.HOSTING_USER_CARD_BUTTON}
                >
                  <AurIcon icon={IconNames.ChevronRight} />
                </Button>
              )}
              <Box className="d-flex ">
                <Typography className="typo-text body2_size mist-700">
                  {t('welcomeScreenSingleClient.reviewWorkspaceUsers')}
                </Typography>
              </Box>
            </Grid>
          }
        </Grid>

        <Box className="indication">
          <Typography className="typo-text body4_size mist-700">
            {t('welcomeScreenSingleClient.indText2')}{' '}
            <AurLightTooltip
              title={hostingUserSummaryDate ? t('common.yearMonthDate') : ''}
            >
              <span>
                {hostingUserSummaryDate
                  ? getUTCDateTimeForDisplay(hostingUserSummaryDate)
                  : ''}
              </span>
            </AurLightTooltip>
          </Typography>
          <Typography className="typo-text body4_size mist-700">
            {t('welcomeScreenSingleClient.indText3')}
          </Typography>
        </Box>
      </AurAccordion>
    </Box>
  );
};

export default WorkspaceOverView;
